import {
  useLayoutEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
  useContext,
} from 'react';
import { useChatLayoutStore, ChatLayout } from './hooks/useChatLayoutStore';
import { ChatTaskAudit } from './ChatTaskAudit';
import { useChatStoreExtract } from '../Chat/hooks/ChatStore.hooks';
import { GlobalStepWidth } from '../Chat/hooks/types';
import { ChatRendererProps } from './renderer/types';
import { ChatInlineRenderer } from './renderer/ChatInlineRenderer';
import ContributorFeedbackWrapper from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/ContributorFeedbackWrapper';
import ContributorFeedbackHeader from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/ContributorFeedbackHeader';
import { withStoreProviders } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils/store';
import { ContributorFeedbackProvider } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { useLogMemoryToDD } from 'react-scale/lib/memoryLogger';

// eslint-disable-next-line restrict-imports/restrict-imports
import NoSSR from '@material-ui/core/NoSsr';
import { Theme } from '@scale/scaleui-radix';

import { ChatEventBus, createEventBus, EventBusContext } from './events';
import ChatEventHandler from './events/ChatEventHandler';
import { ChatTheme, useChatThemeStore } from './hooks/useChatThemeStore';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';
import { ChatTaskContext } from './contexts/ChatTaskContext';
import ChatInteractionSkeleton from './components/ChatInteractionSkeleton';

type ChatInteractionProps = ChatRendererProps & {
  enableLayoutOverride?: boolean;
  stepWidth?: GlobalStepWidth;
  demoMode?: boolean;
};

export interface ChatInteractionHandle {
  eventBus: ChatEventBus;
}

const ChatInteractionComponent = forwardRef<ChatInteractionHandle, ChatInteractionProps>(
  function ChatInteractionComponent(
    {
      readOnly,
      collapsibleInstruction,
      enableLayoutOverride = false,
      preventScrollToBottom,
      displayStage,
      stepWidth,
      demoMode = false,
    },
    ref,
  ) {
    const taskContext = useContext(ChatTaskContext);

    const { isLoading: isPreviewLoading } = useUnleashFeatureFlagWithLoading(
      'chat-preview-migration',
      { projectId: taskContext.projectId },
      false,
      true,
      {
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
      },
    );

    const setLayout = useChatLayoutStore(state => state.setLayout);
    const setStepWidth = useChatStoreExtract(state => state.setStepWidth);
    const layout = useChatLayoutStore(state => state.layout);

    const [eventBus] = useState(createEventBus);

    const theme = useChatThemeStore(state => state.theme);

    useImperativeHandle(ref, () => ({ eventBus }));
    useLogMemoryToDD('ChatInteractionComponent', 0.1);

    useLayoutEffect(() => {
      if (enableLayoutOverride && layout) {
        setLayout(layout);
        setStepWidth(
          stepWidth ?? layout === ChatLayout.MultiColumn
            ? GlobalStepWidth.Full
            : GlobalStepWidth.Dynamic,
        );
        return;
      }

      setLayout(ChatLayout.Inline);
      setStepWidth(stepWidth ?? GlobalStepWidth.Dynamic);

      // NOTE: Initial render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const MultiTurnComponent = useMemo(() => {
      let Component = ChatInlineRenderer;
      if (enableLayoutOverride && layout) {
        switch (layout) {
          case ChatLayout.Inline:
            Component = ChatInlineRenderer;
            break;
          case ChatLayout.MultiColumn:
            Component = ChatTaskAudit;
            break;
        }
      }
      return Component;
    }, [enableLayoutOverride, layout]);

    const preventScrollProp = layout === ChatLayout.Inline ? { preventScrollToBottom } : {};

    if (isPreviewLoading) {
      return <ChatInteractionSkeleton />;
    }

    return (
      <NoSSR>
        <Theme
          appearance={theme === ChatTheme.Light ? 'light' : 'dark'}
          className="!bg-transparent"
        >
          <EventBusContext.Provider value={eventBus}>
            <ChatEventHandler />
            <ContributorFeedbackWrapper>
              <ContributorFeedbackHeader />
              <MultiTurnComponent
                readOnly={readOnly}
                collapsibleInstruction={collapsibleInstruction}
                {...preventScrollProp}
                displayStage={displayStage}
                demoMode={demoMode}
              />
            </ContributorFeedbackWrapper>
          </EventBusContext.Provider>
        </Theme>
      </NoSSR>
    );
  },
);

export const ChatInteraction = withStoreProviders(
  ChatInteractionComponent,
  ContributorFeedbackProvider,
);
